class ProductBundle {
  constructor() {
    this.bundleContainer = document.querySelector('.product-bundle-container');
    if (!this.bundleContainer) {
      return;
    }
    
    this.bundleGalleriesContainer = document.querySelector('.bundle-galleries-container');
    this.addToCartButton = this.bundleContainer.querySelector('.add-bundle-to-cart');
    this.bundleProductsData = this.getBundleProductsData();
    
    // Get existing cart components like the theme does - with safety checks
    this.cartNotification = typeof SHTHelper !== 'undefined' ? SHTHelper.qs("sht-cart-noti") : null;
    this.cartDrawer = typeof SHTHelper !== 'undefined' ? SHTHelper.qs("sht-cart-drwr") : null;
    this.cartDrawerForm = typeof SHTHelper !== 'undefined' ? SHTHelper.qs("sht-cart-drwr-frm") : null;
    this.headerCartStatus = typeof SHTHelper !== 'undefined' ? SHTHelper.qs("#headerCartStatus") : null;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeFirstProducts();
  }

  bindEvents() {
    // Handle bundle product selection (color swatches)
    if (this.bundleContainer) {
      this.bundleContainer.addEventListener('change', (e) => {
        if (e.target.classList.contains('bundle-product-radio')) {
          this.handleProductSelection(e.target);
        }
        // Handle variant selection within products
        else if (e.target.classList.contains('js-bundle-variant-radio-item') || 
                 e.target.classList.contains('bundle-variant-select')) {
          this.handleVariantSelection(e.target);
        }
      });
      
      // Handle click events for variant radio buttons (better delegation)
      this.bundleContainer.addEventListener('click', (e) => {
        if (e.target.classList.contains('js-bundle-variant-option-label') || 
            e.target.closest('.js-bundle-variant-option-value')) {
          // Find the associated radio input
          const optionValue = e.target.closest('.js-bundle-variant-option-value');
          const radioInput = e.target.querySelector('.js-bundle-variant-radio-item') || 
                            (optionValue ? optionValue.querySelector('.js-bundle-variant-radio-item') : null);
          
          if (radioInput && !radioInput.disabled) {
            // Small delay to ensure the radio is checked
            setTimeout(() => {
              if (radioInput.checked) {
                this.handleVariantSelection(radioInput);
              }
            }, 10);
          }
        }
      });
    }

    // Handle add to cart button
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.addBundleToCart();
      });
    } else {
      console.warn('Bundle add to cart button not found');
    }
  }

  handleProductSelection(radio) {
    // Get group index from the parent bundle color option div
    const colorOption = radio.closest('.bundle-color-option');
    const groupIndex = colorOption ? colorOption.dataset.groupIndex : null;
    const productId = radio.value;
    
    if (!groupIndex) {
      console.error('Could not find group index for radio button');
      return;
    }
    
    // Find the group container
    const group = this.bundleContainer.querySelector(`[data-group-index="${groupIndex}"]`);
    
    if (group) {
      // Update the metafield display for this group
      this.updateBundleMetafieldDisplay(groupIndex, colorOption);
      
      // Hide all variant containers in this group
      const allVariantContainers = group.querySelectorAll('.bundle-product-variants');
      
      allVariantContainers.forEach((container, index) => {
        container.classList.add('hidden');
      });
      
      // Find and show the variant container for the selected product
      // Try multiple selectors to ensure we find it
      let selectedVariantContainer = group.querySelector(`.bundle-product-variants[data-product-id="${productId}"]`);
      
      // Alternative selector in case the above doesn't work
      if (!selectedVariantContainer) {
        const allContainers = group.querySelectorAll('.bundle-product-variants');
        for (let container of allContainers) {
          if (container.dataset.productId === productId) {
            selectedVariantContainer = container;
            break;
          }
        }
      }
      
      if (selectedVariantContainer) {
        selectedVariantContainer.classList.remove('hidden');
        
        // Reset and initialize variant selections for the newly selected product
        this.initializeProductVariants(productId, groupIndex, selectedVariantContainer);
        
        // Update variant options availability for the newly selected product
        this.updateVariantOptions(productId, groupIndex);
      } else {
        console.error('Could not find variant container for product ID:', productId);
      }
    } else {
      console.error('Could not find group container for index:', groupIndex);
    }
    
    // Update gallery for this specific group
    this.updateGroupGallery(groupIndex, productId);
  }

  initializeProductVariants(productId, groupIndex, variantContainer) {
    // CRITICAL: First, uncheck ALL radio buttons in ALL variant containers in this group
    // This prevents old selections from lingering when switching products
    const group = this.bundleContainer.querySelector(`[data-group-index="${groupIndex}"]`);
    if (group) {
      const allRadiosInGroup = group.querySelectorAll('.js-bundle-variant-radio-item');
      allRadiosInGroup.forEach((radio, idx) => {
        radio.checked = false;
      });
    }
    
    // Now initialize variants for the selected product
    const variantRadioGroups = variantContainer.querySelectorAll('.js-variant-radio-container');
    
    variantRadioGroups.forEach((fieldset, fieldsetIndex) => {
      // Double-check: uncheck all radios in this specific fieldset
      const allRadiosInFieldset = fieldset.querySelectorAll('.js-bundle-variant-radio-item');
      
      allRadiosInFieldset.forEach((radio, radioIndex) => {
        radio.checked = false;
      });
      
      // Find and check the first available option
      const firstAvailableRadio = fieldset.querySelector('.js-bundle-variant-radio-item:not([disabled])');
      if (firstAvailableRadio) {
        firstAvailableRadio.checked = true;
        
        // Update the variant option name display
        const optionNameSpan = fieldset.querySelector('.js-bundle-variant-option-name');
        if (optionNameSpan) {
          optionNameSpan.textContent = firstAvailableRadio.value;
        }
      }
    });
    
    // Reset selects to first option
    const variantSelects = variantContainer.querySelectorAll('.bundle-variant-select');
    
    variantSelects.forEach((select, selectIndex) => {
      const firstAvailableOption = select.querySelector('option:not([disabled]):not([value=""])');
      if (firstAvailableOption) {
        select.value = firstAvailableOption.value;
      }
    });
    
    // Force update of availability after initialization
    this.updateVariantOptions(productId, groupIndex);
    
    // Small delay to ensure DOM is updated, then trigger change event
    setTimeout(() => {
      const firstCheckedRadio = variantContainer.querySelector('.js-bundle-variant-radio-item:checked');
      if (firstCheckedRadio) {
        const changeEvent = new Event('change', { bubbles: true });
        firstCheckedRadio.dispatchEvent(changeEvent);
      }
    }, 50);
  }

  handleVariantSelection(element) {
    // Get product ID and group index from the element directly
    let productId = element.dataset.productId;
    let groupIndex = element.dataset.groupIndex;
    
    // If not found on element, try to get from parents
    if (!productId || !groupIndex) {
      const variantContainer = element.closest('.bundle-product-variants');
      const bundleGroup = element.closest('.bundle-group');
      
      if (variantContainer) {
        productId = productId || variantContainer.dataset.productId;
      }
      
      if (bundleGroup) {
        groupIndex = groupIndex || bundleGroup.dataset.groupIndex;
      }
    }
    
    if (productId && groupIndex) {
      // Update the variant option name display if this is a radio button
      if (element.classList.contains('js-bundle-variant-radio-item') && element.checked) {
        const fieldset = element.closest('.js-variant-radio-container');
        if (fieldset) {
          const optionNameSpan = fieldset.querySelector('.js-bundle-variant-option-name');
          if (optionNameSpan) {
            optionNameSpan.textContent = element.value;
          }
        }
      }
      
      // Update variant options availability
      this.updateVariantOptions(productId, groupIndex);
      
      // Update gallery for variant-specific images in current active gallery
      const groupGalleryContainer = this.bundleGalleriesContainer?.querySelector(`[data-group-index="${groupIndex}"]`);
      const activeProductGallery = groupGalleryContainer?.querySelector('.bundle-gallery-product.active');
      if (activeProductGallery) {
        this.updateVariantImages(activeProductGallery, productId, groupIndex);
      }
    } else {
      console.warn('Could not find product ID or group index for variant selection');
    }
  }

  updateVariantOptions(productId, groupIndex) {
    const productData = this.getProductData(productId);
    if (!productData) {
      console.warn('No product data found for product ID:', productId);
      return;
    }
    
    const group = this.bundleContainer.querySelector(`[data-group-index="${groupIndex}"]`);
    if (!group) {
      console.warn('No group found for group index:', groupIndex);
      return;
    }
    
    const variantContainer = group.querySelector(`.bundle-product-variants[data-product-id="${productId}"]`);
    if (!variantContainer) {
      console.warn('No variant container found for product ID:', productId);
      return;
    }
    
    // Get currently selected options
    const selectedOptions = [];
    
    // Collect from radio buttons
    const checkedRadios = variantContainer.querySelectorAll('.js-bundle-variant-radio-item:checked');
    checkedRadios.forEach(radio => {
      const optionIndex = parseInt(radio.dataset.optionIndex);
      selectedOptions[optionIndex] = radio.value;
    });
    
    // Collect from selects
    const variantSelects = variantContainer.querySelectorAll('.bundle-variant-select');
    variantSelects.forEach(select => {
      const optionIndex = parseInt(select.dataset.optionIndex);
      if (select.value) {
        selectedOptions[optionIndex] = select.value;
      }
    });
    
    // Update availability for each option
    const optionContainers = variantContainer.querySelectorAll('.js-variant-radio-container');
    optionContainers.forEach((container, optionIndex) => {
      this.updateOptionAvailability(productData, variantContainer, optionIndex, selectedOptions);
    });
  }

  updateOptionAvailability(productData, variantContainer, targetOptionIndex, selectedOptions) {
    // Find all option values for this option position
    const optionElements = variantContainer.querySelectorAll(`[data-option-index="${targetOptionIndex}"]`);
    
    optionElements.forEach(element => {
      let optionValue;
      let isSelect = false;
      
      if (element.tagName === 'SELECT') {
        isSelect = true;
        // Handle select options
        Array.from(element.options).forEach(option => {
          if (option.value === '') return; // Skip empty option
          
          optionValue = option.value;
          const isAvailable = this.isOptionCombinationAvailable(productData, selectedOptions, targetOptionIndex, optionValue);
          
          option.disabled = !isAvailable;
          if (!isAvailable && option.textContent && !option.textContent.includes('unavailable')) {
            option.textContent = `${optionValue} - unavailable`;
          } else if (isAvailable && option.textContent && option.textContent.includes('unavailable')) {
            option.textContent = optionValue;
          }
        });
      } else if (element.classList.contains('js-bundle-variant-option-value')) {
        // Handle radio button container
        const radio = element.querySelector('.js-bundle-variant-radio-item');
        if (radio) {
          optionValue = radio.value;
          const isAvailable = this.isOptionCombinationAvailable(productData, selectedOptions, targetOptionIndex, optionValue);
          
          // Update visual state
          if (isAvailable) {
            element.classList.remove('product-option--unavailable');
            radio.disabled = false;
          } else {
            element.classList.add('product-option--unavailable');
            radio.disabled = true;
          }
        }
      }
    });
  }

  isOptionCombinationAvailable(productData, selectedOptions, targetOptionIndex, targetOptionValue) {
    // Create a test combination with the target option value
    const testOptions = [...selectedOptions];
    testOptions[targetOptionIndex] = targetOptionValue;
    
    // Check if any variant matches this combination and is available
    return productData.some(variant => {
      const matches = testOptions.every((option, index) => {
        if (!option) return true; // If no option selected for this position, it matches anything
        return variant[`option${index + 1}`] === option;
      });
      
      return matches && variant.available;
    });
  }

  updateBundleMetafieldDisplay(groupIndex, colorOption) {
    // Find the metafield display span for this group
    const group = this.bundleContainer.querySelector(`[data-group-index="${groupIndex}"]`);
    if (!group) return;
    
    const metafieldSpan = group.querySelector('.js-bundle-selected-option-name');
    if (!metafieldSpan) return;
    
    // Get the metafield value from the selected color option
    const metafieldValue = colorOption.dataset.productMetafield;
    
    // Update the display
    if (metafieldValue) {
      metafieldSpan.textContent = metafieldValue;
      metafieldSpan.classList.remove('hide-empty');
    } else {
      metafieldSpan.textContent = '';
      metafieldSpan.classList.add('hide-empty');
    }
  }

  updateGroupGallery(groupIndex, productId) {
    
    // Update Desktop Gallery (inside bundleGalleriesContainer)
    if (this.bundleGalleriesContainer) {
      const desktopGroupGallery = this.bundleGalleriesContainer.querySelector(`[data-group-index="${groupIndex}"]`);
      if (desktopGroupGallery) {
        this.updateGalleryContainer(desktopGroupGallery, productId, 'desktop');
      }
    }
    
    // Update Mobile Gallery (inside the bundle group content)
    const bundleGroup = this.bundleContainer.querySelector(`[data-group-index="${groupIndex}"]`);
    if (bundleGroup) {
      const mobileGroupGallery = bundleGroup.querySelector('.bundle-group-gallery');
      if (mobileGroupGallery) {
        this.updateGalleryContainer(mobileGroupGallery, productId, 'mobile');
      }
    }
  }
  
  updateGalleryContainer(galleryContainer, productId, type) {
    
    // Hide all product galleries in this container
    const allProductGalleries = galleryContainer.querySelectorAll('.bundle-gallery-product');
    allProductGalleries.forEach(gallery => {
      gallery.style.display = 'none';
      gallery.classList.remove('active');
    });
    
    // Show the specific product gallery
    const targetProductGallery = galleryContainer.querySelector(`[data-product-id="${productId}"]`);
    if (targetProductGallery) {
      targetProductGallery.style.display = '';
      targetProductGallery.classList.add('active');
      
      // Refresh the bundle gallery in the newly active product gallery
      this.refreshBundleGallery(targetProductGallery);
      
      // Update the gallery when variant changes (for variant-specific images)
      this.updateVariantImages(targetProductGallery, productId, galleryContainer.dataset.groupIndex);
      
    } else {
      console.warn(`Could not find ${type} gallery for product ${productId}`);
    }
  }
  
  updateVariantImages(productGallery, productId, groupIndex) {
    // This function handles variant-specific image updates in the grid layout
    // Since we're using a 2-image grid, we want to ensure the right images are shown
    
    const bundleGroup = this.bundleContainer.querySelector(`[data-group-index="${groupIndex}"]`);
    if (!bundleGroup) return;
    
    const variantContainer = bundleGroup.querySelector(`.bundle-product-variants[data-product-id="${productId}"]:not(.hidden)`);
    if (!variantContainer) return;
    
    // Get currently selected variant options
    const selectedOptions = [];
    const checkedRadios = variantContainer.querySelectorAll('.js-bundle-variant-radio-item:checked');
    checkedRadios.forEach(radio => {
      const optionIndex = parseInt(radio.dataset.optionIndex);
      selectedOptions[optionIndex] = radio.value;
    });
    
    const selects = variantContainer.querySelectorAll('.bundle-variant-select');
    selects.forEach(select => {
      const optionIndex = parseInt(select.dataset.optionIndex);
      if (select.value) {
        selectedOptions[optionIndex] = select.value;
      }
    });
    
    // If this is a variant change, we could potentially update images here
    // For now, the grid will show the first 2 images of the product
    // This can be extended if variant-specific image switching is needed
    
  }

  refreshBundleGallery(productGallery) {
    // Since we're using a grid layout instead of carousel, we just need to ensure
    // the gallery is properly visible and any lazy loading is triggered
    
    // Find any lazy-loaded images in the gallery and trigger loading
    const lazyImages = productGallery.querySelectorAll('img[loading="lazy"], img.js-image-lazy');
    lazyImages.forEach(img => {
      // Trigger intersection observer or other lazy loading mechanisms
      if (img.dataset.src && !img.src) {
        img.src = img.dataset.src;
      }
      
      // Remove loading attribute to force immediate loading for visible gallery
      if (img.hasAttribute('loading')) {
        img.removeAttribute('loading');
      }
    });
    
    // Trigger any resize events that might be needed for proper image sizing
    const galleryImages = productGallery.querySelectorAll('.bundle-gallery-images img');
    galleryImages.forEach(img => {
      if (img.complete) {
        // Image is already loaded, dispatch load event
        img.dispatchEvent(new Event('load'));
      }
    });
    
    // Force a reflow to ensure proper layout
    productGallery.offsetHeight;
    
  }



  getSelectedProducts() {
    const selectedProducts = [];
    
    // Find all bundle groups - use a more specific selector
    const groups = this.bundleContainer.querySelectorAll('.bundle-group[data-group-index]');
    
    groups.forEach((group, groupIdx) => {
      const groupIndex = group.dataset.groupIndex;
      
      // Find the ONE selected product radio in this specific group
      const selectedProductRadios = group.querySelectorAll('.bundle-product-radio:checked');
      
      if (selectedProductRadios.length > 1) {
        console.warn(`Multiple product radios checked in group ${groupIndex}! Taking first one.`);
      }
      
      const selectedProductRadio = selectedProductRadios[0]; // Take only the first one
      
      if (selectedProductRadio) {
        const productId = selectedProductRadio.value;
        
        // Find the variant container for THIS SPECIFIC product only
        const variantContainer = group.querySelector(`.bundle-product-variants[data-product-id="${productId}"]:not(.hidden)`);
        
        if (!variantContainer) {
          console.warn(`No visible variant container found for product ${productId}`);
          return; // Skip this group if no container found
        }
        
        // Collect selected options from THIS container only
        const selectedOptions = [];
        
        // Check radio button variants
        const fieldsets = variantContainer.querySelectorAll('.js-variant-radio-container');
        
        fieldsets.forEach((fieldset, fieldsetIdx) => {
          const checkedRadios = fieldset.querySelectorAll('.js-bundle-variant-radio-item:checked');
          
          if (checkedRadios.length > 1) {
            console.warn(`Multiple radios checked in fieldset ${fieldsetIdx}! Using first one.`);
          }
          
          const checkedRadio = checkedRadios[0]; // Take only the first one
          if (checkedRadio) {
            const optionIndex = parseInt(checkedRadio.dataset.optionIndex);
            const optionValue = checkedRadio.value;
            
            // Ensure we don't overwrite an existing option
            if (selectedOptions[optionIndex] !== undefined) {
              console.warn(`Option index ${optionIndex} already set, overwriting`);
            }
            
            selectedOptions[optionIndex] = optionValue;
          }
        });
        
        // Check select variants  
        const selects = variantContainer.querySelectorAll('.bundle-variant-select');
        
        selects.forEach((select, selectIdx) => {
          const optionIndex = parseInt(select.dataset.optionIndex);
          const optionValue = select.value;
          
          if (optionValue && optionValue !== '') {
            selectedOptions[optionIndex] = optionValue;
          }
        });
        
        // Add to results - only once per group
        selectedProducts.push({
          groupIndex: parseInt(groupIndex),
          productId: productId,
          selectedOptions: selectedOptions
        });
        
      } else {
        console.warn(`No selected product radio found in group ${groupIndex}`);
      }
    });
    
    // Validate no duplicates
    const productIds = selectedProducts.map(p => p.productId);
    const duplicateIds = productIds.filter((id, index) => productIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      console.error('DUPLICATE PRODUCT IDs FOUND:', duplicateIds);
    }
    
    return selectedProducts;
  }

  collectBundleItems() {
    const items = [];
    const selectedProducts = this.getSelectedProducts();
    
    // DO NOT add the main product - bundles should only contain bundle items
    // Removed: mainProductVariantId logic
    
    // Add selected bundle products only
    selectedProducts.forEach(item => {
      const productData = this.getProductData(item.productId);
      if (productData) {
        const variant = this.findVariantByOptions(productData, item.selectedOptions);
        if (variant) {
          // Check if this variant is already in the items (avoid duplicates)
          const isDuplicate = items.some(existingItem => existingItem.id === variant.id);
          if (!isDuplicate) {
            items.push({
              id: variant.id,
              quantity: 1
            });
          } else {
          }
        } else {
          console.warn(`No variant found for product ${item.productId} with options:`, item.selectedOptions);
        }
      } else {
        console.warn(`No product data found for product ID: ${item.productId}`);
      }
    });
    
    return items;
  }

  findVariantByOptions(productData, selectedOptions) {
    // If no variants available
    if (!productData || productData.length === 0) {
      console.warn('No product data available');
      return null;
    }
    
    // If only one variant, return it (no options to match)
    if (productData.length === 1) {
      return productData[0];
    }
    
    // If no options selected, return first available variant
    if (!selectedOptions || selectedOptions.length === 0) {
      const firstAvailable = productData.find(variant => variant.available);
      return firstAvailable || productData[0];
    }
    
    // Find exact match for selected options
    const exactMatch = productData.find(variant => {
      const isMatch = selectedOptions.every((option, index) => {
        if (!option) return true; // Skip empty option slots
        const variantOption = variant[`option${index + 1}`];
        const matches = variantOption === option;
        return matches;
      });
      return isMatch;
    });
    
    if (exactMatch) {
      return exactMatch;
    }
    
    // Fallback: return first available variant if no exact match
    const fallback = productData.find(variant => variant.available);
    return fallback || productData[0];
  }

  getProductData(productId) {
    // Check all groups for this product
    for (const groupKey in this.bundleProductsData) {
      if (this.bundleProductsData[groupKey][productId]) {
        return this.bundleProductsData[groupKey][productId];
      }
    }
    return null;
  }

  getBundleProductsData() {
    const script = this.bundleContainer.querySelector('.bundle-products-data');
    if (script) {
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Error parsing bundle products data:', error);
        return {};
      }
    }
    return {};
  }

  initializeFirstProducts() {
    // Initialize first selected products for each group
    const groups = this.bundleContainer.querySelectorAll('[data-group-index]');
    groups.forEach(group => {
      const groupIndex = group.dataset.groupIndex;
      const firstProductRadio = group.querySelector('.bundle-product-radio:checked');
      
      if (firstProductRadio) {
        const productId = firstProductRadio.value;
        
        // Initialize metafield display for first product
        const firstColorOption = firstProductRadio.closest('.bundle-color-option');
        if (firstColorOption) {
          this.updateBundleMetafieldDisplay(groupIndex, firstColorOption);
        }
        
        // Initialize variant container for first product
        const selectedVariantContainer = group.querySelector(`[data-product-id="${productId}"]`);
        if (selectedVariantContainer) {
          // Ensure it's visible and properly initialized
          selectedVariantContainer.classList.remove('hidden');
          this.initializeProductVariants(productId, groupIndex, selectedVariantContainer);
        }
        
        // Initialize gallery for first product
        this.updateGroupGallery(groupIndex, productId);
      }
    });
    
    // Initial galleries are already set up by individual updateGroupGallery calls above
  }

  async addBundleToCart() {
    if (!this.addToCartButton) return;
    
    // Get selected bundle items
    const bundleItems = this.collectBundleItems();
    
    if (bundleItems.length === 0) {
      console.error('No bundle items selected');
      return;
    }

    try {
      // Set loading state
      this.addToCartButton.classList.add('loading');
      this.addToCartButton.disabled = true;
      
      // Collect sections to render - same as theme
      const sectionsToRender = [
        ...(this.cartNotification && this.cartNotification.getSectionsToRender ? this.cartNotification.getSectionsToRender() : []),
        ...(this.cartDrawer && this.cartDrawer.getSectionsToRender ? this.cartDrawer.getSectionsToRender() : [])
      ];
      
      // Create FormData like the theme does
      const formData = new FormData();
      
      // Add items in the format Shopify expects for multiple items
      bundleItems.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
      });
      
      // Add sections if we have any - match theme format exactly
      if (sectionsToRender.length) {
        formData.append('sections', sectionsToRender.map(section => section.id));
        formData.append('sections_url', window.location.pathname);
      }
      
      // Use the same fetch config as the theme exactly
      SHTHelper.fetchConfigHTTP.body = formData;
      const response = await fetch("" + routes.cart_add_url, SHTHelper.fetchConfigHTTP);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.text();
      const parsedData = JSON.parse(responseData);
      
      // Update cart sections like the theme does
      if (this.cartNotification && parsedData.sections) {
        // For bundles, use the main product's variant ID to show the main product in notification
        // The cart notification expects this to match a cart line item variant ID
        const mainProductVariantId = this.bundleContainer.dataset.mainProductVariantId;
        if (mainProductVariantId) {
          parsedData.id = mainProductVariantId;
        } else {
          // Fallback to first bundle item if main product variant ID not available
          const firstBundleItem = parsedData.items && parsedData.items[0];
          parsedData.id = firstBundleItem ? firstBundleItem.variant_id : this.bundleContainer.dataset.mainProductId;
        }
        this.cartNotification.renderContents(parsedData);
      }
      
      if (this.cartDrawer && parsedData.sections) {
        this.cartDrawer.renderContents(parsedData);
      }
      
      // Update cart status in header
      if (this.headerCartStatus && parsedData.sections) {
        const headerCartHtml = parsedData.sections[this.headerCartStatus.dataset.sectionId];
        if (headerCartHtml) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(headerCartHtml, 'text/html');
          const newCartStatus = doc.querySelector('#headerCartStatus');
          if (newCartStatus) {
            this.headerCartStatus.innerHTML = newCartStatus.innerHTML;
          }
        }
      }
      
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
    } finally {
      // Remove loading state
      this.addToCartButton.classList.remove('loading');
      this.addToCartButton.disabled = false;
      SHTHelper.forceUpdateCartStatus({ id: this.bundleContainer.dataset.mainProductVariantId }); // Use main product ID from container

    }
  }
}

// Export for potential external use
window.ProductBundle = ProductBundle; 

// Initialize ProductBundle when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductBundle();
});

// Also initialize in Shopify theme editor
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ProductBundle();
  });
} 